import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { sponsorsQueryOptions, type Sponsor } from "@/lib/sponsors.queries";
import { createSponsor, deleteSponsor, updateSponsor } from "@/lib/sponsors.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ExternalLink, Image as ImageIcon, Handshake } from "lucide-react";
import { MediaField } from "@/components/admin/MediaField";
import { Field } from "@/components/admin/Field";
import { FormDialog, SaveButton, useDirty } from "@/components/admin/FormDialog";
import { MEDIA_TARGETS } from "@/lib/media-buckets";

export const Route = createFileRoute("/_authenticated/admin/sponsors")({
  component: SponsorsAdmin,
});

type Tier = "Platinum" | "Gold" | "Silver" | "Partner";

type FormState = {
  id?: string;
  name: string;
  tier: Tier;
  logo_url: string;
  website_url: string;
  blurb: string;
  is_active: boolean;
};

const emptyForm: FormState = {
  name: "",
  tier: "Partner",
  logo_url: "",
  website_url: "",
  blurb: "",
  is_active: true,
};

const TIER_RANK: Record<Tier, number> = { Platinum: 0, Gold: 1, Silver: 2, Partner: 3 };

function SponsorsAdmin() {
  const { data: rows = [] } = useQuery(sponsorsQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createSponsor);
  const update = useServerFn(updateSponsor);
  const del = useServerFn(deleteSponsor);

  const [open, setOpen] = useState(false);
  // True while the MediaField's library picker (sibling modal) is up.
  const [pickerBusy, setPickerBusy] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const dirty = useDirty(form, open);

  const sorted = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          TIER_RANK[a.tier] - TIER_RANK[b.tier] ||
          a.name.localeCompare(b.name),
      ),
    [rows],
  );

  const saveMut = useMutation({
    mutationFn: async (f: FormState) => {
      const payload = {
        name: f.name.trim(),
        tier: f.tier,
        logo_url: f.logo_url || null,
        website_url: f.website_url || null,
        blurb: f.blurb || null,
        is_active: f.is_active,
      };
      if (f.id) return update({ data: { ...payload, id: f.id } });
      return create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sponsors"] });
      setOpen(false);
      setForm(emptyForm);
      toast.success("Saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const toggleMut = useMutation({
    mutationFn: (s: Sponsor) =>
      update({
        data: {
          id: s.id,
          name: s.name,
          tier: s.tier,
          logo_url: s.logo_url || "",
          website_url: s.website_url || "",
          blurb: s.blurb || "",
          is_active: !s.is_active,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sponsors"] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sponsors"] });
      toast.success("Removed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const openEdit = (s: Sponsor) => {
    setForm({
      id: s.id,
      name: s.name,
      tier: s.tier,
      logo_url: s.logo_url ?? "",
      website_url: s.website_url ?? "",
      blurb: s.blurb ?? "",
      is_active: s.is_active,
    });
    setOpen(true);
  };

  const openNew = () => {
    setForm({ ...emptyForm });
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Sponsors & Partners</CardTitle>
          <CardDescription>Grouped by tier on the public site.</CardDescription>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> Add sponsor
        </Button>
        <FormDialog
          open={open}
          onOpenChange={setOpen}
          size="md"
          icon={Handshake}
          title={form.id ? "Edit sponsor" : "Add sponsor"}
          context={form.id ? form.name : "New sponsor"}
          dirty={dirty}
          busy={pickerBusy || saveMut.isPending}
          asForm
          onSubmit={() => saveMut.mutate(form)}
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <SaveButton dirty={dirty} pending={saveMut.isPending} />
            </>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" required>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Tier">
                <Select value={form.tier} onValueChange={(v: Tier) => setForm({ ...form, tier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Platinum">Platinum</SelectItem>
                    <SelectItem value="Gold">Gold</SelectItem>
                    <SelectItem value="Silver">Silver</SelectItem>
                    <SelectItem value="Partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <MediaField
                  label="Logo"
                  value={form.logo_url}
                  target={MEDIA_TARGETS.sponsorLogo}
                  onChange={(url) => setForm((f) => ({ ...f, logo_url: url }))}
                  onPickerOpenChange={setPickerBusy}
                />
              </div>
              <Field label="Website URL" full>
                <Input type="url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} />
              </Field>
              <Field label="Status">
                <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
                  <Switch id="sp-active" checked={form.is_active} onCheckedChange={(v: boolean) => setForm({ ...form, is_active: v })} />
                  <Label htmlFor="sp-active" className="cursor-pointer">{form.is_active ? "Active" : "Inactive"}</Label>
                </div>
              </Field>
              <Field label="Short blurb" full>
                <Textarea rows={3} value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} />
              </Field>
          </div>
        </FormDialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Logo</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-28">Tier</TableHead>
              <TableHead className="hidden md:table-cell">Website</TableHead>
              <TableHead className="w-20 text-center">Active</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((s) => (
              <TableRow key={s.id} className={s.is_active ? "" : "opacity-60"}>
                <TableCell>
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="h-8 w-8 rounded object-contain" />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-muted text-muted-foreground">
                      <ImageIcon className="h-4 w-4" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>
                  <Badge variant={s.tier === "Platinum" ? "default" : "secondary"}>{s.tier}</Badge>
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">
                  {s.website_url ? (
                    <a href={s.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
                      Visit <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-center">
                  <Switch checked={s.is_active} onCheckedChange={() => toggleMut.mutate(s)} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Remove ${s.name}?`)) delMut.mutate(s.id); }}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">No sponsors yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

